import { Table } from '@/components/Table';
import { useConfigContext } from '@/context/configContext';
import { RoiInfoTooltip } from '@/dashboards/SpendSummary/components/RoiInfoTooltip';
import { SpendSummaryRow } from '@/dashboards/SpendSummary/helpers/buildSpendSummaryRows';
import {
    useRoiConfig,
    useSpendSummaryContext,
    useSpendSummaryTableRows,
} from '@/dashboards/SpendSummary/hooks';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { cn } from '@/utils';
import { roundToDecimals } from '@/utils/numberUtils';
import { CellContext, Row, createColumnHelper } from '@tanstack/react-table';
import { ReactNode, useCallback, useMemo } from 'react';
import { Typography } from '@analytical-alley/ui';

type NumericColumn = {
    [K in keyof SpendSummaryRow]: SpendSummaryRow[K] extends number ? K : never;
}[keyof SpendSummaryRow];

export const TableCellRenderer = <
    T extends SpendSummaryRow,
    E extends ReactNode,
>({
    getValue,
    className,
    children,
}: CellContext<T, E> & {
    className?: string;
    children?: ReactNode;
    disabled?: boolean;
}) => {
    return (
        <span
            className={cn(
                'flex items-center text-center px-3.5 py-2 h-14',
                className,
            )}
        >
            {children || getValue()}
        </span>
    );
};

// Stateless, so it can live at module scope rather than being rebuilt every
// render — which would defeat the memoisation of `columns` below.
const columnHelper = createColumnHelper<SpendSummaryRow>();

/** The variables/categories breakdown table, with a totals footer. */
export const SpendSummaryTable = () => {
    const { formatNumber } = useConfigContext();
    const { contributionFormatter } = useDashboardGlobalContext();
    const { modelBreakdown } = useSpendSummaryContext();
    const { name } = useRoiConfig();
    const { data, totalRoiFiltered } = useSpendSummaryTableRows();

    const isModelBreakdownCategory = modelBreakdown === 'category';

    const calculateSum = useCallback(
        <Key extends NonNullable<NumericColumn>>(
            rows: Row<SpendSummaryRow>[],
            id: Key,
        ): number => {
            return rows.reduce((sum, row) => sum + row.original[id], 0);
        },
        [],
    );

    const columns = useMemo(
        () => [
            columnHelper.accessor('category', {
                header: 'Category',
                cell: (info) => {
                    return <TableCellRenderer {...info} />;
                },
                footer: 'Total',
                meta: {
                    header: {
                        className: 'h-14',
                    },
                    footer: {
                        className: isModelBreakdownCategory
                            ? 'text-left'
                            : 'text-right',
                    },
                },
            }),
            // Only the variable breakdown names the variable; the category
            // breakdown has one row per category.
            ...(isModelBreakdownCategory
                ? []
                : [
                      columnHelper.accessor('variable', {
                          header: 'Variable',
                          cell: (info) => {
                              return (
                                  <TableCellRenderer
                                      {...info}
                                      className="justify-center"
                                  />
                              );
                          },
                          meta: {
                              header: {
                                  className: 'text-center',
                              },
                          },
                      }),
                  ]),
            columnHelper.accessor('contribution', {
                header: () => (
                    <Typography tooltip="KPI generated by this media category in a selected period.">
                        Contribution
                    </Typography>
                ),
                cell: (info) => {
                    return (
                        <TableCellRenderer {...info} className="justify-center">
                            {contributionFormatter(info.getValue())}
                        </TableCellRenderer>
                    );
                },
                footer: (props) =>
                    contributionFormatter(
                        calculateSum(
                            props.table.getRowModel().rows,
                            'contribution',
                        ),
                    ),
                meta: {
                    header: {
                        className: 'text-center',
                    },
                    footer: {
                        className: 'text-center',
                    },
                },
            }),
            columnHelper.accessor('spend', {
                header: () => (
                    <Typography tooltip="Money spent on this media category over a selected period.">
                        Spend
                    </Typography>
                ),
                cell: (info) => {
                    return (
                        <TableCellRenderer {...info} className="justify-center">
                            {formatNumber(info.getValue())}
                        </TableCellRenderer>
                    );
                },
                footer: (props) => {
                    return formatNumber(
                        calculateSum(props.table.getRowModel().rows, 'spend'),
                    );
                },
                meta: {
                    header: {
                        className: 'text-center',
                    },
                    footer: {
                        className: 'text-center',
                    },
                },
            }),
            columnHelper.accessor('contributionPercentageTotal', {
                header: () => (
                    <Typography tooltip="What share of media contribution was generated by this category.">
                        Contribution % total
                    </Typography>
                ),
                cell: (info) => {
                    return (
                        <TableCellRenderer {...info} className="justify-center">
                            {info.getValue().toFixed(2)}%
                        </TableCellRenderer>
                    );
                },
                footer: (props) =>
                    `${roundToDecimals(calculateSum(props.table.getRowModel().rows, 'contributionPercentageTotal'))}%`,
                meta: {
                    header: {
                        className: 'text-center',
                    },
                    footer: {
                        className: 'text-center',
                    },
                },
            }),
            columnHelper.accessor('roi', {
                header: () => (
                    <div className="flex justify-center items-center gap-1">
                        {name}
                        <RoiInfoTooltip />
                    </div>
                ),
                cell: (info) => {
                    return (
                        <TableCellRenderer
                            {...info}
                            className="justify-center"
                        />
                    );
                },
                footer: () => totalRoiFiltered,
                meta: {
                    header: {
                        className: 'text-center',
                    },
                    footer: {
                        className: 'text-center',
                    },
                },
            }),
        ],
        [
            calculateSum,
            contributionFormatter,
            formatNumber,
            isModelBreakdownCategory,
            name,
            totalRoiFiltered,
        ],
    );

    return (
        <Table
            columns={columns}
            shouldSpanRow
            colSpan={
                isModelBreakdownCategory
                    ? undefined
                    : {
                          footer: { value: 2, index: 0 },
                      }
            }
            data={data}
        />
    );
};
